import parameterize from 'parameterize';
import React from 'react';
import {
  TABLE_TYPE,
  SUBSECTION_TYPE,
  IMAGE_TYPE,
} from '../../consts/content-types';
import {
  SectionContainer,
  SectionContent,
  SectionType,
  SubSectionContainer,
  SubSectionType,
} from '../../shared-styled/blocks/detailedPageBlocks';
import styles from '../../styles/print-media.module.css';
import { snakeToCamel, snakeToTitle } from '../../utils/caseconverters';
import { sectionIsNotEmpty } from '../../utils/content';
import getImageAccessibilityProps from '../../utils/getImageAccessibilityProps';
import Image from '../Image/Image.styled';
import SubSectionTextParsed from '../SubSectionTextParsed/SubSectionTextParsed';
import Table from '../Table/Table';

// Helper function to render content items
const renderContentItem = (item, index) => {
  switch (item.type) {
    case TABLE_TYPE:
      return <Table key={index} value={item.value || item} />;
    case IMAGE_TYPE:
      return (
        <Image
          key={index}
          src={item?.value?.path}
          width={item?.value?.width}
          height={item?.value?.height}
          {...getImageAccessibilityProps(item)}
        />
      );
    default:
      return <SubSectionTextParsed key={index} html={item.value || item} />;
  }
};

const renderSubSectionContent = (content) => {
  if (Array.isArray(content)) {
    return content.map((contentItem, contentIndex) =>
      renderContentItem(contentItem, contentIndex),
    );
  }
  return <SubSectionTextParsed html={content} />;
};

const DetailedSection = ({
  el,
  typeNames,
  isIndented,
  isShownSectionType = true,
  level,
}) => {
  // Normalize `el.value` into an array for consistent rendering
  const values = Array.isArray(el.value) ? el.value : [{ ...el }];
  if (!sectionIsNotEmpty(values) && el?.type !== TABLE_TYPE) return null;
  return (
    <SectionContainer $level={level || 0}>
      {isShownSectionType && (
        <SectionType id={el.type} className={styles.noBreakAfterPrint}>
          {typeNames.length > 1
            ? typeNames[snakeToCamel(el.type)]
            : snakeToTitle(el.type)}
        </SectionType>
      )}
      <SectionContent $isIndented={isIndented}>
        {values.map((val, index) => (
          <SubSectionContainer key={index}>
            {Array.isArray(val.value) ? (
              val.value.map((item, subIndex) => (
                <SubSectionContainer key={subIndex}>
                  {item.type === SUBSECTION_TYPE ? (
                    <>
                      <SubSectionType id={parameterize(item.header)}>
                        {item.header}
                      </SubSectionType>
                      {renderSubSectionContent(item.content)}
                    </>
                  ) : item.type === TABLE_TYPE ? (
                    <Table value={item} />
                  ) : item.type === IMAGE_TYPE ? (
                    <Image
                      src={item?.value?.path}
                      width={item?.value?.width}
                      height={item?.value?.height}
                      {...getImageAccessibilityProps(item)}
                    />
                  ) : (
                    <SubSectionTextParsed html={item} />
                  )}
                </SubSectionContainer>
              ))
            ) : val.type === SUBSECTION_TYPE ? (
              <>
                <SubSectionType id={parameterize(val.value.header)}>
                  {val.value.header}
                </SubSectionType>
                {renderSubSectionContent(val.value.content)}
              </>
            ) : val.type === TABLE_TYPE ? (
              <Table value={val.value} />
            ) : val.type === IMAGE_TYPE ? (
              <Image
                src={val?.value?.path}
                width={val?.value?.width}
                height={val?.value?.height}
                {...getImageAccessibilityProps(val)}
              />
            ) : (
              <SubSectionTextParsed html={val.value} />
            )}
          </SubSectionContainer>
        ))}
      </SectionContent>
    </SectionContainer>
  );
};

DetailedSection.defaultProps = {
  typeNames: [],
};

export default DetailedSection;
